import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Plus, ShieldCheck } from 'lucide-react';
import { api, type Me, type Competition } from '../lib/api';
import { orgRoleDisplayName } from '../lib/roles';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { PageWrapper } from '../components/PageWrapper';
import { useEffectiveRole, useEffectiveMe } from '../lib/viewAs';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';

// MyComp — role-aware comp dashboard.
// - ORG_OWNER/ORG_ADMIN of any org → can create comps under that org.
// - Comp members (any role) → see comps they're part of with role badge.
// Founders + view-as users see the founder-eye view of everything.
//
// COMP-SCOPE RETIREMENT (2026-08-02): the "Apply to help on a comp" block
// that used to sit at the bottom submitted a COMP_ADMIN role request. The
// role spine has no comp scope left — every comp capability folded into
// org.* at ADMIN (src/shared/roleSpine.ts's CAPABILITY_MIN_RANK) and the
// spine backfill auto-denies PENDING COMP_ADMIN requests — so that button
// sent people down a corridor with no door at the end. The live route to the
// same outcome is an ORG_ADMIN request from MyOrg's RoleRequestForm.

export function MyComp() {
  const { data: me, isLoading: meLoading } = useQuery<Me>({
    queryKey: ['me'],
    queryFn: api.me,
  });
  const { data: allComps } = useQuery({
    queryKey: ['competitions'],
    queryFn: api.competitions,
  });
  const effectiveRole = useEffectiveRole(me?.role);
  // View-as-aware copy — the create-comp gate below reads THIS, not raw `me`,
  // so a founder previewing "view as leader" (whose persona strips org
  // memberships) correctly loses the "Create comp" affordance instead of
  // leaking their real org-admin power into the preview. Identity no-op when
  // no view-as is active (lib/viewAs.ts's useEffectiveMe). Matches Sidebar.tsx.
  const effectiveMe = useEffectiveMe(me);

  if (meLoading) return <PageSpinner />;
  if (!me) return null;

  const orgMemberships = effectiveMe?.orgMemberships ?? [];
  // effectiveMe, NOT raw me — "Your comps" listed the FOUNDER's real
  // competitions during a "view as member" preview, which is exactly the
  // leak this list is supposed to help catch.
  const compMemberships = effectiveMe?.competitionMemberships ?? [];
  const compsById = new Map<string, Competition>((allComps ?? []).map(c => [c.id, c]));

  // Orgs where I have admin power (can create comps)
  const adminOrgs = orgMemberships.filter(m => m.role === 'ORG_OWNER' || m.role === 'ORG_ADMIN');

  const noOrgs = orgMemberships.length === 0;

  return (
    <PageWrapper maxWidth="3xl" spacing="md">
      <div className={cn('flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Trophy className="h-6 w-6 text-accent-500" />
        <h1 className="font-display text-3xl font-black uppercase tracking-tight text-white">My Comp</h1>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <HelperCard
          id="mycomp-intro"
          title="What lives here"
          intro={
            effectiveRole === 'founder'
              ? "Founder view: full access to create, join, and manage. Switch ‘view as’ in the bottom-left to see what other roles see."
              : "What you can do here depends on your role in each org. Owners and admins create comps; members see the comps they help run."
          }
          points={[
            { text: 'Create a comp under any org you own or admin.' },
            { text: 'Want to help run comps? Request Org Admin from My Org — comp-level roles are retired.' },
            { text: 'Comps you already help run show below with your role.' },
          ]}
          className="mb-2"
        />
      </div>

      {noOrgs && (
        <EmptyState
          title="Join an org first"
          message="Comps live under organisations. Get added to an org by a founder or an org owner — then you’ll be able to create or apply to comps here."
        />
      )}

      {/* Comps I'm already part of */}
      {compMemberships.length > 0 && (
        <div className={SETTLE} style={settleDelay(2)}>
        <Card className="border-none shadow-none">
          <h2 className="mb-3 flex items-center gap-2 font-display text-xs font-black uppercase tracking-wider text-ink-secondary">
            <ShieldCheck className="h-4 w-4" /> Your comps
          </h2>
          <div className="space-y-2">
            {compMemberships.map(m => {
              const c = compsById.get(m.compId);
              return (
                <div key={m.compId} className="flex flex-wrap items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-2">
                  <span className="font-display text-sm font-black text-white truncate">
                    {c?.name ?? m.compId.replace(/^COMP#/, '')}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-secondary">
                    {m.role}
                  </span>
                  {m.orgId && (
                    <span className="font-mono text-[10px] text-ink-meta">
                      org: {m.orgId.replace(/^ORG#/, '')}
                    </span>
                  )}
                  <Link
                    to={`/me/comp/${encodeURIComponent(m.compId)}`}
                    className="ml-auto font-display text-[10px] font-black uppercase tracking-wider text-accent-500 hover:text-accent-400"
                  >
                    Open →
                  </Link>
                </div>
              );
            })}
          </div>
        </Card>
        </div>
      )}

      {/* Create-comp for orgs I admin */}
      {adminOrgs.length > 0 && (
        <div className={SETTLE} style={settleDelay(3)}>
        <Card className="border-none shadow-none">
          <h2 className="mb-3 flex items-center gap-2 font-display text-xs font-black uppercase tracking-wider text-ink-secondary">
            <Plus className="h-4 w-4" /> Create a competition
          </h2>
          <p className="mb-3 text-xs text-ink-secondary">
            You can create new comps under each org you own or admin.
          </p>
          <div className="space-y-2">
            {adminOrgs.map(m => (
              <div key={m.orgId} className="flex flex-wrap items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-2">
                <span className="font-display text-sm font-black text-white truncate">
                  {m.orgId.replace(/^ORG#/, '')}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-secondary">{orgRoleDisplayName(m.role)}</span>
                <Link
                  to={`/admin/setup?orgId=${encodeURIComponent(m.orgId)}`}
                  className={cn('btn-primary ml-auto inline-flex items-center gap-1', MORPH)}
                >
                  <Plus className="h-3 w-3" strokeWidth={2.5} />
                  Create comp
                </Link>
              </div>
            ))}
          </div>
        </Card>
        </div>
      )}

    </PageWrapper>
  );
}
